import './style/globalStyle.scss';

import { Grommet } from 'grommet';
import { grommet } from 'grommet/themes';
import { deepMerge } from 'grommet/utils';
import { useCallback, useEffect, useErrorBoundary } from 'preact/hooks';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import ABTestProvider from '@/components/ab-test/ABTestProvider';
import GuidedTourOrchestrator from '@/components/AllAccessGuidedTour/GuidedTourOrchestrator';
import GlobalsContextProvider from '@/components/GlobalsContextProvider';
import Main from '@/components/Main';
import RemoteConfigUpdater from '@/components/RemoteConfigUpdater';
import TourOnboarding from '@/components/TourOnboarding';
import persistor from '@/redux/persistor';
import store from '@/redux/store';
import { initializeGA } from '@/shared/ga';
import { initializeMP } from '@/shared/mp';

import { SessionTimerProvider } from './components/advertisements/contexts';
import VideoAnalyticsProvider from './components/video-analytics';
import zoolifeTheme from './grommetTheme';
import { generateTitle, isDev } from './helpers';
import { useIsHabitatTabbed } from './hooks';
import { getDisallowedAutoReloadComponentsCount } from './hooks/disallowAutoReload';
import QueryProvider from './query-provider';
import { useInitOneSignal } from './shared/pushNotifications/hooks';

const customBreakpoints = deepMerge(grommet, zoolifeTheme);

let errorCount = 0;
let currentErrorBucket = 0;

const App = () => {
  const isTabbed = useIsHabitatTabbed();

  useInitOneSignal();

  useErrorBoundary((err) => {
    // TODO: log this somewhere (sentry, logRocket, or GA)
    console.error(err);
    if (isDev()) {
      // exit the app if there are more than 50 errors in 1 second bucket
      const now = Math.floor(Date.now() / 1000);
      if (currentErrorBucket !== now) {
        currentErrorBucket = now;
        errorCount = 0;
      }
      errorCount += 1;
      if (errorCount > 50) {
        throw new Error('too many errors detected');
      }
    }
  });

  useEffect(() => {
    const handleFullscreenMode = () => {
      // document.fullscreenElement will point to the element that
      // is in fullscreen mode if there is one. If there isn't one,
      // the value of the property is null.
      if (document.fullscreenElement) {
        document.body.classList.add('fullscreen', 'group/fullscreen');
      } else {
        document.body.classList.remove('fullscreen', 'group/fullscreen');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenMode);

    initializeGA();
    initializeMP();
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenMode);
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (getDisallowedAutoReloadComponentsCount() === 0 && isTabbed && document.visibilityState === 'visible' && !isDev()) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [isTabbed]);

  const onRouteChange = useCallback(
    ({
      current: {
        props: { title, skipTitle },
      },
    }) => {
      if (!skipTitle && typeof window !== 'undefined') {
        document.title = generateTitle(title);
      }
    },
    [],
  );

  return (
    <QueryProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <GlobalsContextProvider>
            <Grommet theme={customBreakpoints} style={{ background: 'transparent' }}>
              <RemoteConfigUpdater />
              <ABTestProvider>
                <VideoAnalyticsProvider>
                  <SessionTimerProvider>
                    <TourOnboarding>
                      <GuidedTourOrchestrator />
                      <Main onRouteChange={onRouteChange} />
                    </TourOnboarding>
                  </SessionTimerProvider>
                </VideoAnalyticsProvider>
              </ABTestProvider>
            </Grommet>
          </GlobalsContextProvider>
        </PersistGate>
      </Provider>
    </QueryProvider>
  );
};

export default App;
